import { useState } from 'react';
import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTextArea,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2';
import {
  type RexConnectionDetails,
  type RexDisconnectionCampaignDetails,
  useDisconnectRexListing,
} from '@urbanx/agx.support.client';
import { useInvalidateUseGetCampaignActivityLogs } from '@urbanx/agx.activities.client';
import { useInvalidateUseGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { useInvalidateUseGetCrmListingByCampaignId } from '@urbanx/agx.crm.client';

interface DisconnectRexListingDialogProps {
  isOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  campaign: RexDisconnectionCampaignDetails;
  listing: RexConnectionDetails;
  onDisconnected: () => Promise<void>;
}

export default function DisconnectRexListingDialog({
  isOpen,
  setIsDialogOpen,
  campaign,
  listing,
  onDisconnected,
}: DisconnectRexListingDialogProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const { pushToast } = useToast();
  const invalidateCrmListing = useInvalidateUseGetCrmListingByCampaignId();
  const invalidateActivity = useInvalidateUseGetCampaignActivityLogs();
  const invalidateListing = useInvalidateUseGetListingByCampaignId();
  const showError = (failure: unknown) =>
    setError(
      typeof failure === 'string'
        ? failure
        : 'Unable to finish the request. Refresh status to check the connection.'
    );
  const { disconnectRexListing, disconnectRexListingPending } = useDisconnectRexListing({
    onSuccess: async () => {
      pushToast({ message: 'Rex listing disconnected', type: 'success' });
      setIsDialogOpen(false);
      await onDisconnected();
      await invalidateActivity(campaign.campaignId);
      await invalidateListing(campaign.campaignId);
      await invalidateCrmListing(campaign.agencyId, campaign.campaignId);
    },
    onError: (failure) => {
      console.error('Rex disconnection request failed', failure);
      showError(failure);
    },
  });

  return (
    <AgxModal
      id="disconnect-rex-dialog"
      title="Disconnect Rex listing?"
      type="single"
      variant="withDivider"
      actionButtonContainerStyle="withDivider"
      isOpen={isOpen}
      setIsDialogOpen={(open) => {
        if (!disconnectRexListingPending) setIsDialogOpen(open);
      }}
      content={
        <AgxSpacer gap="spacing-lg" padding="spacing-4xl" fill>
          <AgxTypography id="disconnect-rex-summary" type="body" size="medium">
            Disconnect Rex listing {listing.listingId} from {campaign.propertyAddress}.
          </AgxTypography>
          <AgxTypography id="disconnect-rex-effect" type="body" size="small">
            The listing will remain in Rex. Further changes must be made directly in Rex.
          </AgxTypography>
          <label htmlFor="disconnect-rex-reason">Reason</label>
          <AgxTextArea
            id="disconnect-rex-reason"
            noHeader
            required
            maxLength={2000}
            rows={4}
            stretch
            disabled={disconnectRexListingPending}
            defaultValue={reason}
            onInputValueChange={({ value }) => setReason(value)}
          />
          {error && <div role="alert">{error}</div>}
        </AgxSpacer>
      }
      actionButtons={
        <AgxSpacer direction="horizontal" gap="spacing-md">
          <AgxButton
            id="disconnect-rex-cancel"
            text="Cancel"
            variant="hollow"
            disabled={disconnectRexListingPending}
            onClick={() => setIsDialogOpen(false)}
          />
          <AgxButton
            id="disconnect-rex-confirm"
            variant="primary"
            text={disconnectRexListingPending ? 'Disconnecting' : 'Disconnect listing'}
            disabled={disconnectRexListingPending || !reason.trim()}
            onClick={() => {
              setError('');
              void disconnectRexListing({
                campaignId: campaign.campaignId,
                listingId: listing.listingId,
                eTag: listing.eTag,
                reason: reason.trim(),
              }).catch(showError);
            }}
          />
        </AgxSpacer>
      }
    />
  );
}
